import PropTypes from "prop-types";
import React, { useRef, useState, useMemo } from "react";

import { testLocator } from "@/lib/helpers/test-locator";

import { ERROR_MESSAGE, SHOW_COUNTER_THRESHOLD } from "../constants";

import "./styles.scss";

const ExpandableMessageInput = (props) => {
  const { maxLength } = props;
  const [currentMessageText, setCurrentMessageText] = useState("");
  const inputElement = useRef(null);

  const handleInput = () => {
    props.channel.startTyping();
    const text = inputElement.current.innerText;

    setCurrentMessageText(text === "\n" ? "" : text);
    props.channel.endTyping();
  };

  const resetMessageText = () => {
    setCurrentMessageText("");
    inputElement.current.textContent = "";
  };

  const handleTyping = (event) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      }
      if (maxLength && currentMessageText.length > maxLength) {
        event.preventDefault();

        return;
      }
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (maxLength && currentMessageText.length > maxLength) {
      return;
    }
    // Prevent submitting empty or whitespace-only messages
    // Note: handleInput normalizes "\n" to "", so we check trim() instead
    if (currentMessageText.trim() === "") {
      resetMessageText();

      return;
    }
    props.submitHandler(currentMessageText.trim());
    resetMessageText();
  };

  const charCount = currentMessageText.length;
  const isOverLimit = useMemo(() => maxLength && charCount > maxLength, [maxLength, charCount]);

  const showCharCounter = useMemo(
    () => maxLength && (charCount / maxLength >= SHOW_COUNTER_THRESHOLD || isOverLimit),
    [maxLength, charCount, isOverLimit],
  );

  const inputClassName = `mb-0 h-auto form-control content-editable-input-container ${
    isOverLimit ? "input-error" : ""
  }`;

  const buttonClassName = `btn btn-outline-secondary send-button ${
    isOverLimit ? "send-button--disabled text-muted" : ""
  }`;

  return (
    <div className="w-100 tw:p-5">
      <div className="input-group">
        <p
          aria-describedby={
            showCharCounter
              ? isOverLimit
                ? "message-error message-counter"
                : "message-counter"
              : undefined
          }
          aria-invalid={isOverLimit}
          aria-label="Enter a message..."
          className={inputClassName}
          contentEditable={true}
          data-e2e="Messages__messageInput"
          onInput={handleInput}
          onKeyDown={handleTyping}
          ref={inputElement}
          role="textbox"
          {...testLocator("messageInput")}
        />

        <div className="input-group-append">
          <span
            className={buttonClassName}
            data-e2e="Messages__sendMessageButton"
            onClick={!isOverLimit ? handleSubmit : undefined}
            {...testLocator("sendMessageButton")}
          >
            Send
          </span>
        </div>
      </div>
      {showCharCounter && (
        <div className="message-footer tw:text-xs">
          {isOverLimit && (
            <div className="tw:text-text-error-primary" id="message-error">
              {ERROR_MESSAGE}
            </div>
          )}
          <div
            className={`${isOverLimit ? "tw:text-text-error-primary" : "tw:text-text-secondary"} ${!isOverLimit ? "tw:ml-auto" : ""}`}
            id="message-counter"
          >
            {charCount} / {maxLength}
          </div>
        </div>
      )}
    </div>
  );
};

ExpandableMessageInput.propTypes = {
  channel: PropTypes.object.isRequired,
  maxLength: PropTypes.number,
  submitHandler: PropTypes.func.isRequired,
};

export default ExpandableMessageInput;
